// Cookie notice / analytics choice persisted in this browser. Only a current,
// explicit schema-2 choice can enable analytics; legacy acknowledgments, corrupt,
// expired or other-policy records count as "no choice".

export const CONSENT_STORAGE_KEY = 'cookie_consent';
export const CONSENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const OPEN_COOKIE_PREFERENCES_EVENT = 'roade:open-cookie-preferences';
// Bump when the analytics disclosure changes so earlier grants stop counting.
const CONSENT_SCHEMA = 2;

interface ConsentRecord {
  schema?: number;
  documentVersion?: string;
  analytics?: unknown;
  timestamp?: string;
}

let policyVersion: string | null = null;
const listeners = new Set<() => void>();
let globalListenersInstalled = false;
// A choice whose write failed still applies to this page, until another tab chooses.
let unsavedRecord: ConsentRecord | null = null;

const notify = () => listeners.forEach((listener) => listener());

const readRecord = (): ConsentRecord | null => {
  if (unsavedRecord) return unsavedRecord;
  try {
    const value = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) ?? 'null');
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
};

const isCurrent = (record: ConsentRecord | null): record is ConsentRecord =>
  !!record
  && policyVersion !== null
  && record.documentVersion === policyVersion
  && Date.now() - Date.parse(record.timestamp ?? '') <= CONSENT_TTL_MS;

const writeRecord = (record: ConsentRecord) => {
  const value = {
    ...record,
    documentVersion: policyVersion ?? undefined,
    essential: true,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
    unsavedRecord = null;
  } catch {
    // Storage full or blocked: the choice applies to this page only, and an older grant must not outlive it.
    unsavedRecord = value;
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      // Storage blocked entirely; nothing stored can outlive the page.
    }
  }
};

/** Active cookie-policy version; until it is known nothing counts as a choice. */
export const setConsentPolicyVersion = (version: string) => {
  if (version === policyVersion) return;
  policyVersion = version;
  notify();
};

/** False while the policy version is still loading, when no choice can be judged yet. */
export const isConsentPolicyKnown = (): boolean => policyVersion !== null;

/** true/false for a current explicit analytics choice, null when the user has to choose. */
export const getAnalyticsConsent = (): boolean | null => {
  const record = readRecord();
  return isCurrent(record) && record.schema === CONSENT_SCHEMA && typeof record.analytics === 'boolean'
    ? record.analytics
    : null;
};

/** Any current record, including the essential-only acknowledgment used while analytics is off. */
export const hasCurrentCookieNotice = (): boolean => isCurrent(readRecord());

export const saveAnalyticsConsent = (granted: boolean) => {
  writeRecord({ schema: CONSENT_SCHEMA, analytics: granted });
  notify();
};

export const saveCookieNoticeAcknowledgment = () => {
  writeRecord({ analytics: false });
  notify();
};

export const openCookiePreferences = () => {
  window.dispatchEvent(new Event(OPEN_COOKIE_PREFERENCES_EVENT));
};

/** Same-tab saves, other-tab changes and tab resume (expiry) all notify. */
export const subscribeAnalyticsConsent = (listener: () => void): (() => void) => {
  if (!globalListenersInstalled) {
    globalListenersInstalled = true;
    window.addEventListener('storage', (event) => {
      if (event.key === CONSENT_STORAGE_KEY || event.key === null) {
        unsavedRecord = null;
        notify();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') notify();
    });
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
